import React, { useState, useEffect } from 'react';
import api from '../services/apiService';
import { Helmet } from 'react-helmet';
import Select from 'react-select';
import DatePicker, { registerLocale } from 'react-datepicker';
import de from 'date-fns/locale/de';
import 'react-datepicker/dist/react-datepicker.css';

registerLocale('de', de);

const parseLocalDate = value => {
  if (!value) return null;

  const [year, month, day] = String(value).split('-').map(Number);
  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatLocalDate = date => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getIsoWeekNumber = date => {
  const utcDate = new Date(Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  ));
  const weekDay = utcDate.getUTCDay() || 7;
  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - weekDay);
  const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
  return Math.ceil((((utcDate - yearStart) / 86400000) + 1) / 7);
};

const getCurrentLocalDateTime = () => {
  const now = new Date();
  const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString();

  return {
    date: localDateTime.slice(0, 10),
    time: localDateTime.slice(11, 16),
  };
};

const createInitialActivity = pipelineInstId => ({
  type: '',
  description: '',
  ...getCurrentLocalDateTime(),
  status: 'offen',
  responsibleId: '',
  pipelineInstId: pipelineInstId || '',
  assetId: '',
  customerId: '',
  contactId: '',
  prospectId: '',
  prospectContactId: '',
  followUpDate: '',
  followUpTime: '',
});

const activityToEditState = activity => ({
  id: activity.id,
  type: activity.type || '',
  description: activity.description || '',
  date: activity.date ? String(activity.date).slice(0, 10) : '',
  time: activity.time ? String(activity.time).slice(0, 5) : '',
  status: activity.status || 'offen',
  responsibleId: activity.responsibleId ?? '',
  pipelineInstId: activity.pipelineInstId ?? '',
  assetId: activity.assetId ?? '',
  customerId: activity.customerId ?? '',
  contactId: activity.contactId ?? '',
  prospectId: activity.prospectId ?? '',
  prospectContactId: activity.prospectContactId ?? '',
  followUpDate: activity.followUpDate || '',
  followUpTime: activity.followUpTime
    ? String(activity.followUpTime).slice(0, 5)
    : '',
});

const ActivityForm = ({
  pipelineInstId,
  customer,
  contact,
  prospect,
  prospectContact,
  showAddRow = false,
  showFilters = true,
  disableFilterPersistence = false,
  focusActivityId = null,
  onActivitySaved,
  onActivityDeleted,
  onEditCancel,
}) => {
  const [activities, setActivities] = useState([]);
  const [instances, setInstances] = useState([]);
  const [assets, setAssets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [attachmentsByActivity, setAttachmentsByActivity] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 25; // oder was du willst
  const [allContacts, setAllContacts] = useState([]);
  useEffect(() => {
  api.get('/contacts')  // Dieser Endpoint MUSS ALLE Kontakte liefern!
    .then(res => setAllContacts(res.data))
    .catch(() => setAllContacts([]));
}, []);
  

  const [newActivity, setNewActivity] = useState(() => createInitialActivity(pipelineInstId));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
    // Style-Definitionen direkt HIER
  const rowStyle = {
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
    alignItems: 'center',
    flexWrap: 'nowrap',
    overflowX: 'auto',
    marginBottom: '14px',
    marginLeft: '10px',
    marginRight: '10px'
  };
const defaultFilter = {
  pipelineInstId: '',
  customerId: '',
  responsibleId: '',
  contactId: '',
  type: '',
  status: '',
  dateFrom: '',
  dateTo: '',
  description: '',
};

const [filter, setFilter] = useState(() => {
  // ⬇️ Nur lesen, wenn NICHT disableFilterPersistence
  if (!disableFilterPersistence) {
    const stored = localStorage.getItem('activityFormFilters');
    return stored ? JSON.parse(stored) : defaultFilter;
  }
  return defaultFilter;
});

// ⬇️ Nur speichern, wenn NICHT disableFilterPersistence
useEffect(() => {
  if (!disableFilterPersistence) {
    localStorage.setItem('activityFormFilters', JSON.stringify(filter));
  }
}, [filter, disableFilterPersistence]);


  // Aktivitäten und Stammdaten laden
  useEffect(() => {
  const fetchActivities = async () => {
    setLoading(true);
    try {
      // Query-Parameter bauen
      const params = {
        ...filter,
        limit,
        page: currentPage,
      };
      if (pipelineInstId) params.pipelineInstId = pipelineInstId;

      if (focusActivityId) {
        const res = await api.get(`/activities/${focusActivityId}`);
        setActivities(res.data ? [res.data] : []);
        setTotalPages(1);
      } else {
        const res = await api.get('/activities', { params });
        setActivities(res.data.rows || []); // <- Array der Aktivitäten
        setTotalPages(res.data.pages || 1);  // <- Anzahl Seiten vom Backend
      }
    } catch (error) {
      setError('Fehler beim Laden der Aktivitäten.');
    }
    setLoading(false);
  };
  fetchActivities();
  // Wichtig: auch bei Änderung von currentPage oder Filter!
}, [pipelineInstId, focusActivityId, JSON.stringify(filter), currentPage]);

useEffect(() => {
  if (!focusActivityId) return;
  const focusedActivity = activities.find(
    activity => String(activity.id) === String(focusActivityId)
  );
  if (!focusedActivity) return;

  setEditingActivity(previous => (
    previous?.id === focusedActivity.id
      ? previous
      : activityToEditState(focusedActivity)
  ));
}, [focusActivityId, activities]);

// Stammdaten laden (nur 1x am Anfang)
useEffect(() => {
  const fetchMasterData = async () => {
    try {
      const [insts, custs, usrs] = await Promise.all([
        api.get('/pipeline-instances'),
        api.get('/customers'),
        api.get('/users')
      ]);
      setInstances(Array.isArray(insts.data.rows) ? insts.data.rows : []);
      setCustomers(Array.isArray(custs.data.rows) ? custs.data.rows : custs.data);
      setUsers(Array.isArray(usrs.data.rows) ? usrs.data.rows : usrs.data);
    } catch (err) {
      // Fehlerbehandlung optional
    }
  };
  fetchMasterData();
}, []);


 // Kontakte für Kunden (aus Modal, neu, Bearbeiten oder Filter) laden
useEffect(() => {
  // Versuche, einen Kunden auszuwählen (Modal, neues Activity, Edit, oder Filter)
  const customerId =
    (customer && customer.id) ||
    filter.customerId ||
    newActivity.customerId ||
    (editingActivity && editingActivity.customerId);

  if (customerId) {
    api.get(`/contacts/customer/${customerId}`)
      .then(res => setContacts(res.data))
      .catch(() => setContacts([]));
  } else if (prospect?.id) {
    api.get(`/prospects/${prospect.id}/contacts`)
      .then(res => setContacts(res.data))
      .catch(() => setContacts([]));
  } else {
    setContacts([]);
  }
}, [
  customer && customer.id,
  filter.customerId,
  newActivity.customerId,
  editingActivity && editingActivity.customerId,
  prospect && prospect.id
]);




useEffect(() => {
  const fetchAllAttachments = async () => {
    if (!activities.length) return;
    const map = {};
    await Promise.all(
      activities.map(async (a) => {
        try {
          // Passe ggf. den Endpunkt an! Query: /attachments?documentId=...
          const res = await api.get(`/attachments/by-activity/${a.id}`);
          map[a.id] = res.data || [];
        } catch {
          map[a.id] = [];
        }
      })
    );
    setAttachmentsByActivity(map);
  };
  fetchAllAttachments();
}, [activities]);


  // Hinzufügen
const handleAddActivity = async () => {
  const {
    type,
    date,
    time,
    description,
    status,
    responsibleId,
    assetId,
    file,
    followUpDate,
    followUpTime,
  } = newActivity;

  if (!type || !date || !description) {
    alert('Bitte Typ, Datum und Beschreibung angeben.');
    return;
  }
  if (followUpTime && !followUpDate) {
    alert('Bitte für die Wiedervorlage mit Uhrzeit auch ein Datum angeben.');
    return;
  }

  // 1. Zuerst Activity anlegen
  const payload = {
    type,
    description,
    date,
    time: time || null,
    status,
    responsibleId: responsibleId || null,
    assetId: assetId || null,
    pipelineInstId: pipelineInstId || null,
    customerId: (typeof customer !== "undefined" && customer) ? customer.id : (newActivity.customerId || null),
    contactId: newActivity.contactId || contact?.id || null,
    prospectId: prospect ? prospect.id : (newActivity.prospectId || null),
    prospectContactId: newActivity.prospectContactId || prospectContact?.id || null,
    followUpDate: followUpDate || null,
    followUpTime: followUpTime || null,
  };

  let activityResponse;
  try {
    activityResponse = await api.post('/activities', payload);
    setActivities(prev => [activityResponse.data, ...prev]);
  } catch (error) {
    alert(error.response?.data?.error || 'Fehler beim Hinzufügen der Aktivität.');
    return;
  }

  // 2. Falls eine Datei ausgewählt ist, Anhang hochladen
  if (file) {
    const formData = new FormData();
    formData.append('documentType', 'activity');
formData.append('activityId', activityResponse.data.id);   // <-- Nicht documentId!
formData.append('file', file);

    try {
      await api.post('/attachments', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // Optional: Du kannst hier nochmal die Attachments für diese Activity laden/anzeigen
    } catch {
      alert('Datei konnte nicht hochgeladen werden!');
    }
  }

  // 3. Felder zurücksetzen
  setNewActivity({
    ...createInitialActivity(pipelineInstId),
    file: null,
  });
};


  // Löschen
  const handleDeleteActivity = async (id) => {
    if (!window.confirm('Wirklich löschen?')) return;
    try {
      await api.delete(`/activities/${id}`);
      setActivities(prev => prev.filter(a => a.id !== id));
      if (onActivityDeleted) onActivityDeleted(id);
    } catch {
      alert('Fehler beim Löschen der Aktivität.');
    }
  };

  // Bearbeiten starten
  const handleEditClick = (a) => setEditingActivity(activityToEditState(a));

  // Bearbeitung abbrechen
  const handleCancelEdit = () => {
    if (focusActivityId && onEditCancel) {
      onEditCancel();
      return;
    }
    setEditingActivity(null);
  };

  // Edit-Felder aktualisieren
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditingActivity(prev => ({ ...prev, [name]: value }));
    if (name === 'customerId') {
      setContacts([]);
      setEditingActivity(prev => ({ ...prev, contactId: '' }));
      api.get(`/contacts?customerId=${value}`)
        .then(res => setContacts(res.data))
        .catch(() => setContacts([]));
    }
  };

  // Speichern der Änderungen
  const handleSaveActivity = async () => {
    const { id, type, date, description } = editingActivity;
    if (!type || !date || !description) {
      alert('Bitte Typ, Datum und Beschreibung angeben.');
      return;
    }
    if (editingActivity.followUpTime && !editingActivity.followUpDate) {
      alert('Bitte für die Wiedervorlage mit Uhrzeit auch ein Datum angeben.');
      return;
    }
    try {
      const payload = {
        type: editingActivity.type,
        description: editingActivity.description,
        date: editingActivity.date,
        time: editingActivity.time || null,
        status: editingActivity.status,
        responsibleId: editingActivity.responsibleId || null,
        pipelineInstId: editingActivity.pipelineInstId || null,
        assetId: editingActivity.assetId || null,
        customerId: editingActivity.customerId || null,
        contactId: editingActivity.contactId || null,
        prospectId: editingActivity.prospectId || null,
        prospectContactId: editingActivity.prospectContactId || null,
        followUpDate: editingActivity.followUpDate || null,
        followUpTime: editingActivity.followUpTime || null,
      };
      const response = await api.put(`/activities/${id}`, payload);
      setActivities(prev => prev.map(a => a.id === id ? response.data : a));
      setEditingActivity(null);
      if (onActivitySaved) onActivitySaved(response.data);
    } catch (error) {
      alert(error.response?.data?.error || 'Fehler beim Aktualisieren der Aktivität.');
    }
  };

  // Datum/Uhrzeit formatter
function formatDate(dateString) {
  if (!dateString) return '';
  const dateOnlyMatch = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    return `${dateOnlyMatch[3]}.${dateOnlyMatch[2]}.${dateOnlyMatch[1]}`;
  }
  const d = new Date(dateString);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

function formatTime(timeString) {
  // Wenn schon ein separates Feld a.time (z.B. '14:00:00'), dann
  if (!timeString) return '';
  return String(timeString).substring(0, 5); // Nur 'HH:mm'
}
function formatTimeOnly(t) {
  // Akzeptiert "HH:mm" oder "HH:mm:ss", gibt "HH:mm" zurück
  if (!t) return '';
  return t.length >= 5 ? t.substring(0, 5) : t;
}

  if (loading) return <div>Lade Aktivitäten...</div>;
  if (error) return <div>{error}</div>;

  // Styles
  const buttonStyle = { fontSize: '15px', padding: '5px 10px', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', marginRight: '5px' };
  const addButtonStyle = { ...buttonStyle, background: '#28a745' };
  const editButtonStyle = { ...buttonStyle, background: '#007bff' };
  const saveButtonStyle = { ...buttonStyle, background: '#28a745' };
  const cancelButtonStyle = { ...buttonStyle, background: '#6c757d' };
  const deleteButtonStyle = { ...buttonStyle, background: '#dc3545' };

  return (
    <div className="activity-form">
      <Helmet>
        <title>Aktivitäten</title>
        <meta name="description" content="CRM Aktivitäten verwalten" />
      </Helmet>
      <h2>Aktivitäten</h2>

{showFilters && (
  <div style={{
    display: 'flex',
    gap: 16,
    margin: '15px 0',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  }}>
    <select
      value={filter.pipelineInstId}
      onChange={e => setFilter(f => ({ ...f, pipelineInstId: e.target.value }))}
      style={{
    minWidth: 100,   // Mindestbreite in Pixeln
    width: 200,      // Fixe Breite in Pixeln (z. B. 240px)
    maxWidth: 200,   // Maximalbreite
  }}
    >
      <option value="">Vorgang</option>
      {instances.map(i => (
        <option key={i.id} value={i.id}>{i.name}</option>
      ))}
    </select>
    <Select
  value={
    customers
      .map(c => ({ value: String(c.id), label: c.company }))
      .find(opt => opt.value === String(filter.customerId)) || null
  }
  onChange={selected =>
    setFilter(f => ({
      ...f,
      customerId: selected ? selected.value : '',
    }))
  }
  options={customers.map(c => ({
    value: String(c.id),    // als String, damit es IMMER passt
    label: c.company,
  }))}
  isClearable
  placeholder="Kunde"
  styles={{
    container: base => ({
      ...base,
      minWidth: 220,
      maxWidth: 360,
      marginTop: -5,
      flex: 1,
    }),
    menu: base => ({
      ...base,
      zIndex: 10000,
    }),
  }}
/>
    <select
      value={filter.responsibleId}
      onChange={e => setFilter(f => ({ ...f, responsibleId: e.target.value }))}
       style={{
    minWidth: 150,   // Mindestbreite in Pixeln
    width: 200,      // Fixe Breite in Pixeln (z. B. 240px)
    maxWidth: 300,   // Maximalbreite
  }}
    >
      <option value="">Verantwortlicher</option>
      {users.map(u => (
        <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
      ))}
    </select>
    <Select
  value={
    contacts
      .map(c => ({
        value: String(c.id),
        label:
          (c.contactFirstName || c.firstName || '') +
          ' ' +
          (c.contactLastName || c.lastName || '') +
          (c.email ? ` (${c.email})` : ''),
      }))
      .find(opt => opt.value === String(filter.contactId)) || null
  }
  onChange={selected =>
    setFilter(f => ({
      ...f,
      contactId: selected ? selected.value : '',
    }))
  }
  options={contacts.map(c => ({
    value: String(c.id),
    label:
      (c.contactFirstName || c.firstName || '') +
      ' ' +
      (c.contactLastName || c.lastName || '') +
      (c.email ? ` (${c.email})` : ''),
  }))}
  isClearable
  placeholder="Kontakt suchen…"
  styles={{
    container: base => ({
      ...base,
      minWidth: 180,
      maxWidth: 360,
      marginTop: -5,
      flex: 1,
    }),
    menu: base => ({
      ...base,
      zIndex: 10000,
    }),
  }}
/>

    <select
      value={filter.type}
      onChange={e => setFilter(f => ({ ...f, type: e.target.value }))}
      style={{ minWidth:'80px', }}
    >
      <option value="">Typ</option>
      <option value="Telefon">Anruf</option>
      <option value="E-Mail">E-Mail</option>
      <option value="Meeting">Meeting</option>
      <option value="Notiz">Notiz</option>
      <option value="Aufgabe">Aufgabe</option>
    </select>
    <select
      value={filter.status}
      
      onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}
      style={{ minWidth:'80px', }}
    >
      <option value="">Status</option>
      <option value="offen">offen</option>
      <option value="erledigt">erledigt</option>
    </select>
    <input
      type="date"
      value={filter.dateFrom}
      onChange={e => setFilter(f => ({ ...f, dateFrom: e.target.value }))}
      placeholder="Von"
    />
    <input
      type="date"
      value={filter.dateTo}
      onChange={e => setFilter(f => ({ ...f, dateTo: e.target.value }))}
      placeholder="Bis"
    />
    <input
      type="text"
      value={filter.description}
      onChange={e => setFilter(f => ({ ...f, description: e.target.value }))}
      placeholder="Beschreibung"
      style={{ minWidth: 150 }}
    />
    
    <button
    style={{ marginTop:'-15px', padding: '5px 12px', background: '#eee', border: '1px solid #bbb', borderRadius: 5 }}
  onClick={() => {
    setFilter({
      pipelineInstId: '',
      customerId: '',
      responsibleId: '',
      contactId: '',
      type: '',
      status: '',
      dateFrom: '',
      dateTo: '',
      description: '',
    });
    setCurrentPage(1); // WICHTIG: Auf Seite 1 zurückspringen
  }}
  
>
  ↩️
</button>
  </div>
)}


      {/* Formular für neue Aktivität */}
       {showAddRow && (
  <div
    className="activity-add-grid"
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
      gap: "10px",
      alignItems: "end",
      marginBottom: "12px",
      marginLeft: 0,
      width: "100%",
      padding: "0 8px"
    }}
  >
        <select
          value={newActivity.type}
          onChange={e => setNewActivity({ ...newActivity, type: e.target.value })}
          style={{
    marginLeft: "0px",
    
  }}
        >
          <option value="">Typ wählen</option>
          <option value="Telefon">Anruf</option>
          <option value="E-Mail">E-Mail</option>
          <option value="Meeting">Meeting</option>
          <option value="Notiz">Notiz</option>
          <option value="Aufgabe">Aufgabe</option>
        </select>
       <input
          type="date"
          value={newActivity.date}
          onChange={e => setNewActivity({ ...newActivity, date: e.target.value })}
          placeholder="Datum"
        />
        <input
          type="time"
          value={newActivity.time}
          onChange={e => setNewActivity({ ...newActivity, time: e.target.value })}
          placeholder="Uhrzeit"
        />
        {/* STATUS als Dropdown */}
        <select
          value={newActivity.status}
          onChange={e => setNewActivity({ ...newActivity, status: e.target.value })}
          style={{
    marginLeft: "0px",
    
  }}
        >
          <option value="offen">offen</option>
          <option value="erledigt">erledigt</option>
        </select>
        <select
          value={newActivity.responsibleId}
          onChange={e => setNewActivity({ ...newActivity, responsibleId: e.target.value })}
          style={{
    marginLeft: "0px",
    
  }}
        >
          <option value="">Verantwortlicher</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
        </select>
        {/* Vorgang, Kunde, Kontakt als fix oder Auswahl */}
        

        

  {/* Kontakt: immer Dropdown */}
  <select
    value={prospect ? newActivity.prospectContactId : newActivity.contactId}
    onChange={e => setNewActivity({
      ...newActivity,
      [prospect ? 'prospectContactId' : 'contactId']: e.target.value,
    })}
    disabled={!(customer?.id || prospect?.id || newActivity.customerId)}
    style={{
    marginLeft: "0px",
    
  }}
  >
    <option value="">Kontakt wählen</option>
    {contacts.map(c => (
      <option key={c.id} value={c.id}>
        {(c.contactFirstName || c.firstName || '') + ' ' + (c.contactLastName || c.lastName || '')}
      </option>
    ))}
  </select>
    
        <textarea
          className="activity-add-description"
          rows={3}
          value={newActivity.description}
          onChange={e => setNewActivity({ ...newActivity, description: e.target.value })}
          placeholder="Beschreibung"
        />
        <input
  className="activity-add-file"
  type="file"
  onChange={e => setNewActivity({ ...newActivity, file: e.target.files[0] })}
/>
        <label className="activity-add-labeled-field">
          <span>Wiedervorlage am:</span>
          <DatePicker
            selected={parseLocalDate(newActivity.followUpDate)}
            onChange={date => setNewActivity(previous => ({
              ...previous,
              followUpDate: formatLocalDate(date),
              followUpTime: date ? previous.followUpTime : '',
            }))}
            dateFormat="dd.MM.yyyy"
            locale="de"
            calendarStartDay={1}
            showWeekNumbers
            weekLabel="KW"
            formatWeekNumber={getIsoWeekNumber}
            placeholderText="TT.MM.JJJJ"
            className="activity-follow-up-date-input"
            calendarClassName="activity-follow-up-datepicker"
            popperClassName="activity-follow-up-datepicker-popper"
            portalId="activity-follow-up-datepicker-portal"
            aria-label="Wiedervorlage am"
          />
        </label>
        <label className="activity-add-labeled-field">
          <span>um:</span>
          <input
            type="time"
            value={newActivity.followUpTime}
            onChange={e => setNewActivity({
              ...newActivity,
              followUpTime: e.target.value,
            })}
            disabled={!newActivity.followUpDate}
          />
        </label>
        <button className="activity-add-button" onClick={handleAddActivity} style={addButtonStyle}>
          Hinzufügen
        </button>
      </div>
      )}

      {/* Tabelle mit Aktivitäten */}
      <div className="activity-table-scroll">
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Typ</th>
            <th>Datum</th>
            <th>Uhrzeit</th>
            <th>Wiedervorlage</th>
            <th>Status</th>
            <th>Verantwortlich</th>
            <th>Vorgang</th>
            
            <th>Kunde</th>
            <th>Kontakt</th>
            <th>Beschreibung</th>
            <th>Anhang</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {activities.map(a => (
            <tr
              key={a.id}
              className={editingActivity?.id === a.id ? 'activity-edit-row' : undefined}
            >
              <td>{a.id}</td>
              <td>
                {editingActivity?.id === a.id ? (
                  <select
                    className="activity-edit-control activity-edit-type"
                    name="type"
                    value={editingActivity.type}
                    onChange={handleEditInputChange}
                  >
                    <option value="">Typ wählen</option>
                    <option value="Telefon">Anruf</option>
                    <option value="E-Mail">E-Mail</option>
                    <option value="Meeting">Meeting</option>
                    <option value="Notiz">Notiz</option>
                    <option value="Aufgabe">Aufgabe</option>
                  </select>
                ) : a.type}
              </td>
              <td>
                {editingActivity?.id === a.id ? (
                  <input
                    className="activity-edit-control activity-edit-date"
                    type="date"
                    name="date"
                    value={editingActivity.date || ''}
                    onChange={handleEditInputChange}
                  />
                ) : formatDate(a.date)}
              </td>
               <td>
  {editingActivity?.id === a.id ? (
    <input
      className="activity-edit-control activity-edit-time"
      type="time"
      name="time"
      value={editingActivity.time || ''}
      onChange={e => setEditingActivity(prev => ({ ...prev, time: e.target.value }))}
    />
  ) : (a.time ? formatTimeOnly(a.time) : '-')}
</td>
              <td>
                {editingActivity?.id === a.id ? (
                  <div className="activity-edit-follow-up">
                    <DatePicker
                      className="activity-edit-control activity-edit-date"
                      name="followUpDate"
                      selected={parseLocalDate(editingActivity.followUpDate)}
                      onChange={date => setEditingActivity(previous => ({
                        ...previous,
                        followUpDate: formatLocalDate(date),
                        followUpTime: date ? previous.followUpTime : '',
                      }))}
                      dateFormat="dd.MM.yyyy"
                      locale="de"
                      calendarStartDay={1}
                      showWeekNumbers
                      weekLabel="KW"
                      formatWeekNumber={getIsoWeekNumber}
                      placeholderText="TT.MM.JJJJ"
                      calendarClassName="activity-follow-up-datepicker"
                      popperClassName="activity-follow-up-datepicker-popper"
                      portalId="activity-follow-up-datepicker-portal"
                      aria-label="Wiedervorlage am"
                    />
                    <input
                      className="activity-edit-control activity-edit-time"
                      type="time"
                      name="followUpTime"
                      value={editingActivity.followUpTime || ''}
                      onChange={handleEditInputChange}
                      disabled={!editingActivity.followUpDate}
                      aria-label="Wiedervorlage um"
                    />
                  </div>
                ) : a.followUpDate ? (
                  <span className="activity-follow-up-value">
                    {formatDate(a.followUpDate)}
                    {a.followUpTime ? `, ${formatTimeOnly(a.followUpTime)} Uhr` : ''}
                  </span>
                ) : '-'}
              </td>
              <td>
                {editingActivity?.id === a.id ? (
                  <select
      className="activity-edit-control activity-edit-status"
      name="status"
      value={editingActivity.status}
      onChange={handleEditInputChange}
    >
      <option value="">Status wählen</option>
      <option value="offen">offen</option>
      <option value="erledigt">erledigt</option>
    </select>
  ) : (a.status || '-')}
              </td>
              <td>
                {users.find(u => u.id === a.responsibleId)?.firstName
                  || a.responsible?.firstName
                  || '-'}
              </td>
              <td>
                {instances.find(i => i.id === a.pipelineInstId)?.name
                  || a.pipelineInstance?.name
                  || '-'}
              </td>
             
              <td>
                {a.prospect
                  ? <em>{a.prospect.company || '-'}</em>
                  : (
                    customers.find(c => c.id === a.customerId)?.company
                    || a.customer?.company
                    || '-'
                  )}
              </td>
             <td>
  {(() => {
    if (a.prospectContact) {
      return `${a.prospectContact.contactFirstName || ''} ${a.prospectContact.contactLastName || ''}`.trim() || '-';
    }
    const foundContact = allContacts.find(c => String(c.id) === String(a.contactId));
    if (!foundContact) return '-';
    const name =
      (foundContact.contactFirstName || foundContact.firstName || '') +
      ' ' +
      (foundContact.contactLastName || foundContact.lastName || '');
    return name.trim() ? name : '-';
  })()}
</td>
              <td>
                {editingActivity?.id === a.id ? (
                  <textarea
                    className="activity-edit-control activity-edit-description"
                    rows={4}
                    name="description"
                    value={editingActivity.description || ''}
                    onChange={handleEditInputChange}
                  />
                ) : (
                  <span className="activity-description-text">{a.description || '-'}</span>
                )}
              </td>
           <td>
  {attachmentsByActivity[a.id] && attachmentsByActivity[a.id].length > 0 ? (
    attachmentsByActivity[a.id].map(att => {
      const fileName = att.filepath.split('/').pop();
      // <-- DAS ist wichtig!
      const previewUrl = `${api.defaults.baseURL}/attachments/preview/${encodeURIComponent(fileName)}`;
      return (
        <a
          key={att.id}
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginRight: 6 }}
          title={att.filename}
        >
          📎 {att.filename}
        </a>
      );
    })
  ) : (
    '-'
  )}
</td>




              <td>
                {editingActivity?.id === a.id ? (
                  <div className="activity-row-actions">
                    <button onClick={handleSaveActivity} style={saveButtonStyle}>Speichern</button>
                    <button onClick={handleCancelEdit} style={cancelButtonStyle}>Abbrechen</button>
                  </div>
                ) : (
                  <div className="activity-row-actions">
                    <button onClick={() => handleEditClick(a)} style={editButtonStyle}>edit</button>
                    <button onClick={() => handleDeleteActivity(a.id)} style={deleteButtonStyle}>X</button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

{/* Pagination */}
{!focusActivityId && (
<div style={{ marginTop: '1rem', textAlign: 'center' }}>
  <button
    onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
    disabled={currentPage === 1}
    style={{ fontSize: '15px', padding: '8px 12px', backgroundColor: 'rgb(155, 155, 155)', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
  >
    Vorherige
  </button>
  <span style={{ margin: '0 1rem' }}>
    Seite {currentPage} von {totalPages}
  </span>
  <button
    onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
    disabled={currentPage >= totalPages}
    style={{ fontSize: '15px', padding: '8px 12px', backgroundColor: 'rgb(155, 155, 155)', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
  >
    Nächste
  </button>
</div>
)}

    </div>
  );
};

export default ActivityForm;
